import { useCallback, useRef } from 'react';
import { distance, pointToSegmentDistance } from '../utils/geometry';
export const ERASER_RADIUS = 0.02;
/** Check if a point is inside a filled shape's bounds (not just near its edges). */
function isPointInsideShape(shape, px, py) {
    switch (shape.type) {
        case 'box':
        case 'text':
            return px >= shape.x && px <= shape.x + shape.width && py >= shape.y && py <= shape.y + shape.height;
        case 'circle': {
            if (shape.rx === 0 || shape.ry === 0)
                return false;
            const dx = (px - shape.cx) / shape.rx;
            const dy = (py - shape.cy) / shape.ry;
            return dx * dx + dy * dy <= 1;
        }
        default:
            return false;
    }
}
function boxToFreeform(shape) {
    const { id, color, x, y, width, height } = shape;
    return {
        id,
        type: 'freeform',
        color,
        points: [
            { x, y },
            { x: x + width, y },
            { x: x + width, y: y + height },
            { x, y: y + height },
            { x, y }, // close the path
        ],
        strokeWidth: 3,
    };
}
function circleToFreeform(shape) {
    const { id, color, cx, cy, rx, ry } = shape;
    const numPoints = 64;
    const points = [];
    for (let i = 0; i <= numPoints; i++) {
        const angle = (i / numPoints) * Math.PI * 2;
        points.push({
            x: cx + rx * Math.cos(angle),
            y: cy + ry * Math.sin(angle),
        });
    }
    return { id, type: 'freeform', color, points, strokeWidth: 3 };
}
function lineToFreeform(shape) {
    const { id, color, x1, y1, x2, y2 } = shape;
    return {
        id,
        type: 'freeform',
        color,
        points: [
            { x: x1, y: y1 },
            { x: x2, y: y2 },
        ],
        strokeWidth: 3,
    };
}
function arrowToFreeform(shape) {
    const { id, color, x1, y1, x2, y2 } = shape;
    return {
        id,
        type: 'freeform',
        color,
        points: [
            { x: x1, y: y1 },
            { x: x2, y: y2 },
        ],
        strokeWidth: 3,
    };
}
function textToFreeform(shape) {
    const { id, color, x, y, width, height } = shape;
    return {
        id,
        type: 'freeform',
        color,
        points: [
            { x, y },
            { x: x + width, y },
            { x: x + width, y: y + height },
            { x, y: y + height },
            { x, y },
        ],
        strokeWidth: 3,
    };
}
function convertToFreeform(shape) {
    switch (shape.type) {
        case 'box':
            return boxToFreeform(shape);
        case 'circle':
            return circleToFreeform(shape);
        case 'line':
            return lineToFreeform(shape);
        case 'arrow':
            return arrowToFreeform(shape);
        case 'freeform':
            return shape;
        case 'text':
            return textToFreeform(shape);
    }
}
function erasePointsFromFreeform(shape, px, py, radius) {
    const result = [];
    let currentSegment = [];
    for (let i = 0; i < shape.points.length; i++) {
        const point = shape.points[i];
        if (distance(point.x, point.y, px, py) < radius) {
            // This point is within the eraser — split here
            if (currentSegment.length >= 2) {
                result.push({
                    ...shape,
                    id: `${shape.id}-${result.length}`,
                    points: [...currentSegment],
                });
            }
            currentSegment = [];
        }
        else {
            // Check if the segment from the previous point to this one crosses the eraser
            if (currentSegment.length > 0) {
                const prev = currentSegment[currentSegment.length - 1];
                if (pointToSegmentDistance(px, py, prev.x, prev.y, point.x, point.y) < radius) {
                    // Segment crosses eraser — split here
                    if (currentSegment.length >= 2) {
                        result.push({
                            ...shape,
                            id: `${shape.id}-${result.length}`,
                            points: [...currentSegment],
                        });
                    }
                    currentSegment = [point];
                    continue;
                }
            }
            currentSegment.push(point);
        }
    }
    // Save the last segment
    if (currentSegment.length >= 2) {
        result.push({
            ...shape,
            id: result.length > 0 ? `${shape.id}-${result.length}` : shape.id,
            points: currentSegment,
        });
    }
    return result;
}
export function useEraser({ shapes, dispatch }) {
    const isErasing = useRef(false);
    const eraseAt = useCallback((px, py) => {
        let modified = false;
        const newShapes = [];
        for (const shape of shapes) {
            // Check if any part of the shape is near the eraser point
            const freeform = convertToFreeform(shape);
            const isNear = freeform.points.some((p, i) => {
                if (distance(p.x, p.y, px, py) < ERASER_RADIUS)
                    return true;
                if (i > 0) {
                    const prev = freeform.points[i - 1];
                    return pointToSegmentDistance(px, py, prev.x, prev.y, p.x, p.y) < ERASER_RADIUS;
                }
                return false;
            });
            // Also check if eraser is inside filled shapes (text, box, circle)
            const isInside = isPointInsideShape(shape, px, py);
            if (!isNear && !isInside) {
                newShapes.push(shape);
                continue;
            }
            // For non-freeform shapes, remove the entire shape
            if (shape.type !== 'freeform') {
                modified = true;
                continue;
            }
            // Freeform: partially erase points near the eraser
            const remaining = erasePointsFromFreeform(shape, px, py, ERASER_RADIUS);
            newShapes.push(...remaining);
            modified = true;
        }
        if (modified) {
            dispatch({ type: 'ERASE_AT', shapes: newShapes });
        }
    }, [shapes, dispatch]);
    const startErasing = useCallback(() => {
        isErasing.current = true;
    }, []);
    const stopErasing = useCallback(() => {
        isErasing.current = false;
    }, []);
    return { eraseAt, startErasing, stopErasing, isErasing: () => isErasing.current };
}
export { convertToFreeform, erasePointsFromFreeform };
